import {
  DEFAULT_USER_PREFERENCES,
  normalizeContactLocalCallWindowMode,
  normalizeContactLocalCallWindowTime,
} from '../admin/userPreferencesAdapter.js';
import { normalizeContactTimeZone } from '../utils/contactTimeZone.js';

export const CONTACT_LOCAL_CALL_WINDOW_DEFERRED_BY = 'contactLocalCallWindow';
export const CONTACT_LOCAL_CALL_WINDOW_HELD_BY = 'contactLocalCallWindowEarly';

/**
 * Determine whether a task belongs to the call-primary Focus Mode family.
 *
 * @param {unknown} value - Raw task type identifier.
 * @returns {boolean} True for CALL, CALL_EMAIL, or CALL_TEXTMESSAGE.
 */
export function isCallPrimaryTaskType(value) {
  const normalized = String(value || '')
    .trim()
    .toUpperCase();
  return (
    normalized === 'CALL' ||
    normalized === 'CALL_EMAIL' ||
    normalized === 'CALL_TEXTMESSAGE' ||
    normalized === 'PHONE' ||
    normalized === 'PHONE_EMAIL' ||
    normalized === 'PHONE_TEXT'
  );
}

/**
 * Normalize the contact-local call-window preference subset.
 *
 * @param {Record<string, unknown>|null|undefined} preferences - Raw preference payload.
 * @returns {{
 *   enabled: boolean,
 *   mode: 'automatic'|'manual',
 *   start: string,
 *   end: string,
 * }} Canonical preference object.
 */
export function normalizeContactLocalCallWindowPreferences(preferences = {}) {
  return {
    enabled: preferences?.contactLocalCallWindowEnabled === true,
    mode: normalizeContactLocalCallWindowMode(
      preferences?.contactLocalCallWindowMode
    ),
    start: normalizeContactLocalCallWindowTime(
      preferences?.contactLocalCallWindowStart,
      DEFAULT_USER_PREFERENCES.contactLocalCallWindowStart
    ),
    end: normalizeContactLocalCallWindowTime(
      preferences?.contactLocalCallWindowEnd,
      DEFAULT_USER_PREFERENCES.contactLocalCallWindowEnd
    ),
  };
}

/**
 * Convert an HH:MM string into minutes since midnight.
 *
 * @param {string} value - Normalized HH:MM string.
 * @returns {number} Minutes since midnight.
 */
function hhmmToMinutes(value) {
  const [hour = 0, minute = 0] = String(value || '')
    .split(':')
    .map((part) => Number.parseInt(part, 10));
  return hour * 60 + minute;
}

/**
 * Format a normalized HH:MM value using a 12-hour clock label.
 *
 * @param {string} value - Normalized HH:MM string.
 * @returns {string} Human-friendly local time label.
 */
function formatWindowClockLabel(value) {
  const [rawHour = 0, rawMinute = 0] = String(value || '')
    .split(':')
    .map((part) => Number.parseInt(part, 10));
  const hour = Number.isFinite(rawHour) ? rawHour : 0;
  const minute = Number.isFinite(rawMinute) ? rawMinute : 0;
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${String(minute).padStart(2, '0')} ${period}`;
}

/**
 * Resolve the contact-local calendar date for the given instant.
 *
 * @param {Date} now - Instant to interpret.
 * @param {string} timeZone - Valid IANA timezone identifier.
 * @returns {string} Contact-local yyyy-mm-dd string or an empty string.
 */
function dateKeyInTimeZone(now, timeZone) {
  const safeTimeZone = normalizeContactTimeZone(timeZone);
  if (!safeTimeZone) return '';

  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: safeTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const parts = formatter.formatToParts(now);
    const lookup = (type) =>
      parts.find((part) => part.type === type)?.value || '';
    const year = lookup('year');
    const month = lookup('month');
    const day = lookup('day');
    return year && month && day ? `${year}-${month}-${day}` : '';
  } catch {
    return '';
  }
}

/**
 * Return the timezone offset in minutes for a UTC date interpreted in `timeZone`.
 *
 * @param {Date} dateObj - UTC date anchor.
 * @param {string} timeZone - Valid IANA timezone identifier.
 * @returns {number} Signed offset in minutes.
 */
function timeZoneOffsetMinutes(dateObj, timeZone) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = Object.fromEntries(
    dtf
      .formatToParts(dateObj)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value])
  );
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );
  return (asUtc - dateObj.getTime()) / 60000;
}

/**
 * Build an ISO timestamp string for a contact-local wall-clock time.
 *
 * @param {Date|string} dateLike - Source instant or contact-local yyyy-mm-dd string.
 * @param {string} timeZone - Valid IANA timezone identifier.
 * @param {number} hour - Local hour.
 * @param {number} minute - Local minute.
 * @param {number} second - Local second.
 * @returns {string} ISO timestamp with offset, or an empty string when invalid.
 */
function isoAtTimeInTimeZone(
  dateLike = new Date(),
  timeZone,
  hour = 8,
  minute = 0,
  second = 0
) {
  const safeTimeZone = normalizeContactTimeZone(timeZone);
  const dayISO =
    typeof dateLike === 'string'
      ? String(dateLike).slice(0, 10)
      : dateKeyInTimeZone(dateLike, safeTimeZone);
  if (!safeTimeZone || !dayISO) return '';

  const [year, month, day] = dayISO.split('-').map(Number);
  if ([year, month, day].some((value) => Number.isNaN(value))) return '';

  const utcBase = new Date(
    Date.UTC(year, month - 1, day, hour, minute, second)
  );
  const offMin = timeZoneOffsetMinutes(utcBase, safeTimeZone);
  const sign = offMin >= 0 ? '+' : '-';
  const pad = (value) =>
    String(Math.trunc(Math.abs(Number(value) || 0))).padStart(2, '0');
  const offset = `${sign}${pad(offMin / 60)}:${pad(offMin % 60)}`;
  return `${dayISO}T${pad(hour)}:${pad(minute)}:${pad(second)}${offset}`;
}

/**
 * Format the given instant using the contact-local clock.
 *
 * @param {Date|string|null|undefined} dateLike - Instant to format.
 * @param {string} timeZone - Valid IANA timezone identifier.
 * @returns {string} Human-friendly local time label or an empty string.
 */
function formatTimeInTimeZone(dateLike, timeZone) {
  const safeTimeZone = normalizeContactTimeZone(timeZone);
  if (!safeTimeZone || !dateLike) return '';

  const dateObj =
    dateLike instanceof Date ? dateLike : new Date(String(dateLike || ''));
  if (Number.isNaN(dateObj.getTime())) return '';

  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: safeTimeZone,
      hour: 'numeric',
      minute: '2-digit',
    }).format(dateObj);
  } catch {
    return '';
  }
}

/**
 * Convert common Firestore/Date/ISO hold values into epoch milliseconds.
 *
 * @param {unknown} value - Candidate timestamp payload.
 * @returns {number|null} Epoch milliseconds or null when unavailable.
 */
function toMillis(value) {
  if (typeof value?.toMillis === 'function') {
    const result = value.toMillis();
    return Number.isFinite(result) ? result : null;
  }
  if (typeof value?.toDate === 'function') {
    const dateObj = value.toDate();
    return dateObj instanceof Date && !Number.isNaN(dateObj.getTime())
      ? dateObj.getTime()
      : null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.getTime();
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string' && value.trim()) {
    const dateObj = new Date(value);
    return Number.isNaN(dateObj.getTime()) ? null : dateObj.getTime();
  }
  return null;
}

/**
 * Read a task hold field from the root payload or nested metadata.
 *
 * @param {Record<string, unknown>|null|undefined} task - Focus Mode task payload.
 * @param {string} key - Hold field name.
 * @returns {unknown} Resolved field value.
 */
function readTaskHoldField(task, key) {
  const meta = task?.metadata || {};
  const nestedMeta = meta?.metadata || {};
  return task?.[key] ?? meta?.[key] ?? nestedMeta?.[key] ?? null;
}

/**
 * Resolve the persisted contact-local early-window hold state for a task.
 *
 * @param {Record<string, unknown>|null|undefined} task - Focus Mode task payload.
 * @param {{ now?: Date }} [options={}] - Evaluation context.
 * @returns {{
 *   active: boolean,
 *   holdBy: string,
 *   holdUntilAt: string,
 *   holdUntilMs: number|null,
 * }} Hold metadata snapshot.
 */
export function getContactLocalCallWindowHold(task, { now = new Date() } = {}) {
  const holdBy = String(readTaskHoldField(task, 'availabilityHoldBy') || '');
  const rawHoldUntil =
    readTaskHoldField(task, 'availabilityHoldUntilAt') ||
    readTaskHoldField(task, 'availabilityHoldUntil');
  const holdUntilMs = toMillis(rawHoldUntil);
  const holdUntilAt =
    typeof rawHoldUntil === 'string'
      ? rawHoldUntil
      : holdUntilMs
        ? new Date(holdUntilMs).toISOString()
        : '';
  return {
    active:
      holdBy === CONTACT_LOCAL_CALL_WINDOW_HELD_BY &&
      Number.isFinite(holdUntilMs) &&
      holdUntilMs > now.getTime(),
    holdBy,
    holdUntilAt,
    holdUntilMs,
  };
}

/**
 * Determine whether a task is still waiting for contact-local call hours to open.
 *
 * @param {Record<string, unknown>|null|undefined} task - Focus Mode task payload.
 * @param {{ now?: Date }} [options={}] - Evaluation context.
 * @returns {boolean} True when the early-window hold is still active.
 */
export function isContactLocalCallWindowHoldActive(
  task,
  { now = new Date() } = {}
) {
  return getContactLocalCallWindowHold(task, { now }).active;
}

/**
 * Resolve the local hour/minute pair for `now` in the provided timezone.
 *
 * @param {Date} now - Instant to interpret.
 * @param {string} timeZone - Valid IANA timezone identifier.
 * @returns {{ minutes: number, label: string }|null} Zoned time data or null.
 */
function getMinutesInTimeZone(now, timeZone) {
  const safeTimeZone = normalizeContactTimeZone(timeZone);
  if (!safeTimeZone) return null;

  try {
    const numericFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: safeTimeZone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const numericParts = numericFormatter.formatToParts(now);
    const lookup = (type) =>
      numericParts.find((part) => part.type === type)?.value || '';
    const rawHour = Number.parseInt(lookup('hour'), 10);
    const hour = rawHour === 24 ? 0 : rawHour;
    const minute = Number.parseInt(lookup('minute'), 10);
    if (!Number.isInteger(hour) || !Number.isInteger(minute)) {
      return null;
    }

    const label = new Intl.DateTimeFormat('en-US', {
      timeZone: safeTimeZone,
      hour: 'numeric',
      minute: '2-digit',
    }).format(now);

    return {
      minutes: hour * 60 + minute,
      label,
    };
  } catch {
    return null;
  }
}

/**
 * Evaluate whether a call-primary task is inside the contact's local call window.
 *
 * @param {{
 *   taskType?: unknown,
 *   preferences?: Record<string, unknown>|null,
 *   contactTimeZone?: unknown,
 *   now?: Date,
 * }} [params={}] - Evaluation inputs.
 * @returns {{
 *   applies: boolean,
 *   windowState: 'early'|'within'|'late'|null,
 *   withinWindow: boolean,
 *   shouldHold: boolean,
 *   shouldDefer: boolean,
 *   mode: 'automatic'|'manual',
 *   contactTimeZone: string|null,
 *   contactLocalTimeLabel: string,
 *   windowStart: string,
 *   windowEnd: string,
 *   windowLabel: string,
 *   nextEligibleAt: string,
 *   nextEligibleLabel: string,
 * }} Runtime decision payload.
 */
export function evaluateContactLocalCallWindow({
  taskType = '',
  preferences = {},
  contactTimeZone = null,
  now = new Date(),
} = {}) {
  const normalizedPreferences =
    normalizeContactLocalCallWindowPreferences(preferences);
  const normalizedTimeZone = normalizeContactTimeZone(contactTimeZone);
  const windowLabel = `${formatWindowClockLabel(normalizedPreferences.start)} to ${formatWindowClockLabel(normalizedPreferences.end)}`;

  if (
    !normalizedPreferences.enabled ||
    !isCallPrimaryTaskType(taskType) ||
    !normalizedTimeZone
  ) {
    return {
      applies: false,
      windowState: null,
      withinWindow: true,
      shouldHold: false,
      shouldDefer: false,
      mode: normalizedPreferences.mode,
      contactTimeZone: normalizedTimeZone,
      contactLocalTimeLabel: '',
      windowStart: normalizedPreferences.start,
      windowEnd: normalizedPreferences.end,
      windowLabel,
      nextEligibleAt: '',
      nextEligibleLabel: '',
    };
  }

  const zoned = getMinutesInTimeZone(now, normalizedTimeZone);
  if (!zoned) {
    return {
      applies: false,
      windowState: null,
      withinWindow: true,
      shouldHold: false,
      shouldDefer: false,
      mode: normalizedPreferences.mode,
      contactTimeZone: normalizedTimeZone,
      contactLocalTimeLabel: '',
      windowStart: normalizedPreferences.start,
      windowEnd: normalizedPreferences.end,
      windowLabel,
      nextEligibleAt: '',
      nextEligibleLabel: '',
    };
  }

  const startMinutes = hhmmToMinutes(normalizedPreferences.start);
  const endMinutes = hhmmToMinutes(normalizedPreferences.end);
  const withinWindow =
    zoned.minutes >= startMinutes && zoned.minutes < endMinutes;
  const windowState = withinWindow
    ? 'within'
    : zoned.minutes < startMinutes
      ? 'early'
      : 'late';
  const nextEligibleAt =
    windowState === 'early'
      ? isoAtTimeInTimeZone(
          now,
          normalizedTimeZone,
          Math.trunc(startMinutes / 60),
          startMinutes % 60,
          0
        )
      : '';
  const nextEligibleLabel =
    windowState === 'early'
      ? formatTimeInTimeZone(nextEligibleAt || now, normalizedTimeZone) ||
        normalizedPreferences.start
      : '';

  return {
    applies: true,
    windowState,
    withinWindow,
    shouldHold: windowState === 'early',
    shouldDefer: windowState === 'late',
    mode: normalizedPreferences.mode,
    contactTimeZone: normalizedTimeZone,
    contactLocalTimeLabel: zoned.label,
    windowStart: normalizedPreferences.start,
    windowEnd: normalizedPreferences.end,
    windowLabel,
    nextEligibleAt,
    nextEligibleLabel,
  };
}
